import { SUPPORTED_LANGUAGES, type Gender, type RoomSettings } from '@tolk/shared';
import type { ClientMessage } from '@tolk/shared';
import type { Teksten } from '../teksten';
import { aanspreekvormLabel, sfeerLabel } from '../teksten';

/**
 * Instellingen aanpassen MIDDEN IN het gesprek.
 *
 * Waarom dit moet kunnen: je merkt pas na drie zinnen dat de toon te stijf
 * staat, of dat de vertaling raar doet en je de correctie wilt uitzetten om te
 * zien wat er werkelijk verstaan werd. Opnieuw een kamer maken betekent
 * opnieuw een link delen, en dan is het gesprek voorbij.
 *
 * WIE WAT MAG. Taal en stem zijn van JOU - ze gaan over jouw kant van het
 * gesprek. Toon, correctie en het doorsturen van je stem gelden voor de kamer,
 * en die mogen allebei aanpassen: het is een gesprek tussen twee gelijken,
 * geen baas en gast. Wat de een omzet, ziet de ander meteen veranderen.
 */
export default function Instellingen({
  t,
  settings,
  opPlekA,
  stuur,
}: {
  t: Teksten;
  settings: RoomSettings;
  opPlekA: boolean;
  stuur: (bericht: ClientMessage) => void;
}) {
  const mijnTaal = opPlekA ? settings.langA : settings.langB;
  const anderTaal = opPlekA ? settings.langB : settings.langA;
  const mijnGeslacht: Gender = opPlekA ? settings.genderA : settings.genderB;

  const hokje = (
    aan: boolean,
    zet: (waarde: boolean) => void,
    titel: string,
    uitleg?: string,
  ): React.ReactElement => (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={aan}
        onChange={(e) => zet(e.target.checked)}
        className="mt-0.5 w-3.5 h-3.5 accent-gio-blue cursor-pointer shrink-0"
      />
      <span className="text-[11px] text-slate-650 leading-relaxed">
        {titel}
        {uitleg !== undefined && <span className="block text-slate-450">{uitleg}</span>}
      </span>
    </label>
  );

  const veld = 'w-full p-1.5 bg-slate-50 border-2 border-slate-250 rounded-lg text-[11px] text-slate-800 cursor-pointer focus:outline-none focus:border-gio-blue transition-colors duration-155';
  const kopje = 'block text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-1';

  return (
    <div className="space-y-3 mt-2 animate-fade-in">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-350">
        {t.alleenVoorJou}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={kopje}>{t.welkeTaalSprekJij}</span>
          <select
            value={mijnTaal}
            onChange={(e) => stuur({ type: 'settings', lang: e.target.value })}
            className={veld}
          >
            {SUPPORTED_LANGUAGES.map((taal) => (
              // De taal van de ANDER staat er niet tussen: twee keer dezelfde
              // taal maakt de kamer zinloos. De server weigert het ook, maar
              // een keuze die je niet mag maken hoor je niet te zien.
              <option key={taal.code} value={taal.code} disabled={taal.code === anderTaal}>
                {taal.flag} {taal.eigenNaam}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className={kopje}>{t.jouwStem}</span>
          <select
            value={mijnGeslacht}
            onChange={(e) => stuur({ type: 'settings', gender: e.target.value as Gender })}
            className={veld}
          >
            <option value="man">{t.man}</option>
            <option value="vrouw">{t.vrouw}</option>
          </select>
        </div>
      </div>

      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-350 pt-1">
        {t.geldtVoorJullieBeiden}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={kopje}>{t.toon}</span>
          <select
            value={settings.aanspreekvorm}
            onChange={(e) =>
              stuur({ type: 'settings', aanspreekvorm: e.target.value as RoomSettings['aanspreekvorm'] })
            }
            className={veld}
          >
            {(['automatisch', 'informeel', 'formeel'] as const).map((waarde) => (
              <option key={waarde} value={waarde}>
                {aanspreekvormLabel(waarde, t)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className={kopje}>{t.sfeer}</span>
          <select
            value={settings.sfeer}
            onChange={(e) =>
              stuur({ type: 'settings', sfeer: e.target.value as RoomSettings['sfeer'] })
            }
            className={veld}
          >
            {(['neutraal', 'zakelijk', 'officieel', 'vriendschappelijk', 'partner'] as const).map(
              (waarde) => (
                <option key={waarde} value={waarde}>
                  {sfeerLabel(waarde, t)}
                </option>
              ),
            )}
          </select>
        </div>
      </div>

      {hokje(
        settings.alleenChat,
        (waarde) => stuur({ type: 'settings', alleenChat: waarde }),
        t.alleenChat,
        t.alleenChatUitleg,
      )}

      {/* Bij alleen chat wordt er niets opgenomen: geen stem om mee te sturen. */}
      {!settings.alleenChat &&
        hokje(
          settings.stemDelen,
          (waarde) => stuur({ type: 'settings', stemDelen: waarde }),
          t.stuurMijnStemMee,
        )}

      {hokje(
        settings.correctie,
        (waarde) => stuur({ type: 'settings', correctie: waarde }),
        t.verbeterSpelfouten,
        t.verbeterSpelfoutenUit,
      )}
    </div>
  );
}
